import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';

// ONLY load LoginPage eagerly - everything else is lazy loaded
import LoginPage from './pages/LoginPage';
import { apiClient, sessionManager } from './api';

// Lazy load the entire authenticated app shell
const AuthenticatedApp = lazy(() => import('./pages/AuthenticatedApp'));

// Minimal loading fallback
const PageLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontSize: '18px',
      color: '#666',
    }}
  >
    <div className="spinner-border text-primary" role="status"></div>
  </div>
);

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  // Helper to get cookie value
  const getCookie = (name: string): string | null => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  // Check for existing session on mount
  React.useEffect(() => {
    const checkAuth = async () => {
      //console.log('[Auth] All cookies:', document.cookie);

      try {
        const response = await apiClient.fetch('/api/auth/check');

        //console.log('[Auth] Response status:', response.status);

        if (response.status === 404) {
          // Auth endpoint doesn't exist - passkey auth is disabled
          //console.log('[Auth] Auth endpoint not found - passkey auth disabled');
          setIsAuthenticated(true);
          setIsLoading(false);
        } else if (response.ok) {
          const data = await response.json();
          const authenticated = data.authenticated === true;
          //console.log('[Auth] Response data:', data, 'authenticated:', authenticated);
          setIsAuthenticated(authenticated);
          setIsLoading(false);
          if (!authenticated) {
            // session is invalid or expired - login required
            //console.warn('[Auth] Session not authenticated or expired - logging out');
            handleLogout();
          } else {
            //console.log('[Auth] Successfully authenticated with existing session');
          }
        } else {
          // Auth endpoint exists but returned error - require login
          //console.warn('[Auth] Auth check failed with status:', response.status);
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      } catch (error) {
        // Network error - fail secure, require login
        console.error('[Auth] Authentication check failed:', error);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionManager.logout();
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        {isAuthenticated ? (
          <AuthenticatedApp onLogout={handleLogout} />
        ) : (
          <LoginPage onLogin={handleLogin} />
        )}
      </Suspense>
    </Router>
  );
};

export default App;
